/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import { generateCode } from '@/utils/generateCode';
import { getRoleKey } from '@/utils/promptSequenceViewDataVariation';
import { iconMap } from '@/ux-common/src/lib/helper';
import {
  AppGraph,
  Component,
  ComponentJSON,
  ComponentsData,
  LinkType,
  NodeType,
  Provider,
  SubDataArrayType,
} from 'types/api-data';
import { OptionsTypes } from 'types/multi-select';
import { IPrompt } from 'types/prompt';
import { ProvidersJSON, SupportedFeature } from 'types/providers';

import { DropdownDataType } from '@/components/components-page/components-config-card';

export const multiSelectDataTransform = (
  data: Component[] | Provider[] | undefined,
  model?: boolean
) => {
  const subData: OptionsTypes = [];
  let id = 0;

  data?.forEach((entity: any) => {
    if (!model || !entity?.properties?.ml_model) {
      if (!model) {
        const name = entity?.component_name || entity?.provider_name;
        const label = entity?.display_name;
        subData.push({ id: id++, value: name, label });
      }
      return; // Just skip this iteration
    }

    const name = entity?.properties?.ml_model;
    const label = entity?.properties?.ml_model;
    subData.push({ id: id++, value: name, label });
  });

  return subData;
};

export const removeFilterDuplicates = (arr: OptionsTypes) => {
  const seen = new Set();
  return arr?.filter((item) => {
    const duplicate = seen.has(item.value);
    seen.add(item.value);
    return !duplicate;
  });
};

export const graphDataTransform = (
  data: AppGraph,
  showAllEnabled?: boolean
): {
  node: NodeType[];
  link: LinkType[];
} => {
  const filterLogical = data.components.filter(
    (item) => item.domain === 'logical'
  );
  let key = 0;
  const subDataArray: SubDataArrayType[] = [];
  filterLogical.forEach((cur) => {
    subDataArray.push({
      key,
      display_name: cur.display_name,
      component_name: cur.component_name,
      type: cur.type,
      status: cur.status,
      in_use: showAllEnabled ? true : (cur.in_use ?? false), // true='Till Last Discovered' view, false=custom range view
    });
    key++;
  });

  key = 0;

  const nodeDataArray: NodeType[] = [];
  const linkDataArray: LinkType[] = [];

  filterLogical.forEach((cur) => {
    nodeDataArray.push({
      key,
      name: cur.display_name,
      source: iconMap[cur.type] ?? iconMap['default'],
      in_use: showAllEnabled ? true : (cur.in_use ?? false), // true='Till Last Discovered' view, false=custom range view
    });
    key++;
  });

  key = 0;

  const filterConsumes = data.dependencies.filter(
    (item) => item.dependency_type === 'consumes'
  );

  const keyLookUp = (name: string) => {
    const obj = subDataArray?.filter((item) => item.component_name === name);

    return obj[0]?.key;
  };

  filterConsumes.forEach((dependency) => {
    linkDataArray.push({
      key: key++,
      from: keyLookUp(dependency.source),
      to: keyLookUp(dependency.target),
    });
  });

  return { node: nodeDataArray, link: linkDataArray };
};

export const formatDuration = (durationMs: number, short?: boolean) => {
  const milliseconds = durationMs % 1000;
  const seconds = Math.floor((durationMs / 1000) % 60);
  const minutes = Math.floor((durationMs / (1000 * 60)) % 60);
  const hours = Math.floor((durationMs / (1000 * 60 * 60)) % 24);
  const days = Math.floor(durationMs / (1000 * 60 * 60 * 24));

  let result = '';

  if (short) {
    if (days > 0) {
      result += `${days}d `;
    }
    if (hours > 0) {
      result += `${hours}h `;
    }
    if (minutes > 0) {
      result += `${minutes}m `;
    }
    if (seconds > 0 || durationMs < 1000) {
      result += `${seconds}s `;
    }
    if (milliseconds > 0 && durationMs < 1000) {
      result += `${milliseconds}ms `;
    }
  } else {
    if (days > 0) {
      result += `${days} day${days > 1 ? 's' : ''} `;
    }
    if (hours > 0) {
      result += `${hours} hour${hours > 1 ? 's' : ''} `;
    }
    if (minutes > 0) {
      result += `${minutes} minute${minutes > 1 ? 's' : ''} `;
    }
    if (seconds > 0 || durationMs < 1000) {
      result += `${seconds} second${seconds > 1 ? 's' : ''} `;
    }
    if (milliseconds > 0 && durationMs < 1000) {
      result += `${milliseconds} millisecond${milliseconds > 1 ? 's' : ''} `;
    }
  }

  return result.trim();
};

export const formatSecondsAndMilliseconds = (durationMs: number) => {
  const milliseconds = durationMs % 1000;
  const seconds = Math.floor((durationMs / 1000) % 60);

  let result = '';

  if (seconds > 0) {
    result += `${seconds}s `;
  }

  if (milliseconds > 0 || seconds === 0) {
    result += `${milliseconds}ms `;
  }

  return result.trim();
};

export const removeDuplicates = (data: SupportedFeature[]) => {
  const uniqueItems: SupportedFeature[] = [];
  const seen = new Set();

  data.forEach((item) => {
    const identifier = `${item.type}-${item.display_name}`;
    if (!seen.has(identifier)) {
      seen.add(identifier);
      uniqueItems.push(item);
    }
  });

  return uniqueItems;
};

// Function to process providers and create an array of objects with supported features
export const processProviders = (data: ProvidersJSON) => {
  const result: SupportedFeature[] = [];

  // Loop through each provider
  data.providers.forEach((provider) => {
    const features = provider.supported_functions;

    // Add each feature to the result array with provider info
    features.forEach((feature) => {
      result.push(feature);
    });
  });

  return result;
};

// Filter array for Components
// Multi-select array for ComponentJSON
export const createFilterArray = (data: ComponentsData | ComponentJSON[]) => {
  const result: string[] = [];
  data?.map((component) =>
    result.push(component.type.slice(0, component.type.indexOf('.')))
  );

  return Array.from(new Set(result));
};

// Function to create name with a random 6 char suffix
export const createNameWithSuffix = (name: string, charNo = 7) => {
  let baseName = name.trim().toLowerCase().replaceAll(' ', '_');

  const maxBaseNameLength = 64 - charNo;
  if (baseName.length > maxBaseNameLength) {
    baseName = baseName.slice(0, maxBaseNameLength);
  }
  const uniqueCode = generateCode();
  return `${baseName}_${uniqueCode}`;
};

export const serviceDropdownDataTransform = (
  data: any[]
): DropdownDataType[] => {
  const seen = new Set<string>();
  return data
    .filter((item) => {
      if (seen.has(item.display_name)) return false;
      seen.add(item.display_name);
      return true;
    })
    .map((item) => ({
      id: item.component_name,
      name: item.display_name,
    }));
};

// Hanlde all incoming data variants for prompt display
export const handlePromptDataVarient = (content: any) => {
  if (Array.isArray(content)) {
    // Handle array of content objects
    if (content.length > 0 && typeof content[0] === 'object') {
      return JSON.stringify(content[0]);
    }
    return content[0];
  } else if (typeof content === 'object' && content !== null) {
    // Handle other objects
    return JSON.stringify(content);
  } else {
    // Handle strings and primitives
    return content;
  }
};

// Helper function to check if the input is a string type, else stringify it.
export const alwaysRenderStringFormatToUI = (input: any) => {
  if (typeof input === 'string') return input;

  if (typeof input === 'object' || Array.isArray(input))
    return JSON.stringify(input);
};

// Function to format prompt input/output with role and content
export function promptFormatting(input?: string | string[]) {
  if (!input) return [];

  const inputs = Array.isArray(input) ? input : [input];

  return inputs.map((item) => {
    try {
      const parsed = new Function(`return [${item}]`)()[0]; // parse "{'human': 'What is an americano?'}"
      const key = Object.keys(parsed)[0];
      return {
        role: getRoleKey(key),
        content: parsed[key],
      };
    } catch (err) {
      // fallback in case of parsing error
      return {
        role: '',
        content: item,
      };
    }
  });
}

// Get role and content from prompt
export const getRoleAndContent = (prompt: IPrompt) => {
  const outputResponse =
    prompt['data.output']?.response ?? prompt?.output?.response;

  const outputDataWithRole = promptFormatting(outputResponse);
  const outputRole = outputDataWithRole[0]?.role || '';
  const outputContent = outputDataWithRole[0]?.content || '';
  const outputString = alwaysRenderStringFormatToUI(outputContent) || '';

  return { outputRole, outputString };
};
